import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { MaskLines, Em, Eyebrow } from "@/components/site/Motion";
import { MagneticButton } from "@/components/site/Magnetic";
import { Stopwatch } from "@/components/site/Stopwatch";
import { TrackLines } from "@/components/site/TrackLines";
import { FlagRow } from "@/components/site/Flags";

const Chip = ({ k, v, tone, testid }) => (
  <div className={`flex items-baseline gap-3 rounded-full border border-[#0E0E10] px-4 py-2 ${tone}`} data-testid={testid}>
    <span className="font-mono-x text-sm font-bold">{k}</span><span className="text-sm">{v}</span>
  </div>
);

const TiltWatch = () => {
  const rx = useSpring(useMotionValue(0), { stiffness: 120, damping: 14 });
  const ry = useSpring(useMotionValue(0), { stiffness: 120, damping: 14 });
  const move = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    ry.set(((e.clientX - r.left) / r.width - 0.5) * 22);
    rx.set(-((e.clientY - r.top) / r.height - 0.5) * 22);
  };
  return (
    <div style={{ perspective: 900 }} onMouseMove={move} onMouseLeave={() => { rx.set(0); ry.set(0); }} className="mx-auto w-64 sm:w-80 lg:w-full lg:max-w-[440px]">
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        initial={{ scale: 0.6, opacity: 0, rotate: -25 }} animate={{ scale: 1, opacity: 1, rotate: 0 }}
        transition={{ duration: 1.3, delay: 0.4, ease: [0.22, 1, 0.36, 1] }} className="relative">
        <div className="absolute inset-[6%] translate-x-4 translate-y-5 rounded-full bg-[#FF4D1A]" style={{ transform: "translateZ(-40px)" }} />
        <Stopwatch />
        <span style={{ transform: "translateZ(60px)" }} className="font-mono-x absolute -left-2 top-[14%] rotate-[-8deg] rounded-sm bg-[#0E0E10] px-3 py-1.5 text-xs text-[#F1ECE2] sm:-left-8">
          <span className="text-[#FF4D1A]">●</span> reply ≤ 1h
        </span>
        <span style={{ transform: "translateZ(80px)" }} className="font-mono-x absolute -right-2 bottom-[10%] rotate-[6deg] rounded-sm bg-[#2234F5] px-3 py-1.5 text-xs text-white sm:-right-6">
          shortlist ≤ 5h
        </span>
      </motion.div>
    </div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBg = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const yWatch = useTransform(scrollYProgress, [0, 1], [0, -90]);
  return (
    <section ref={ref} className="grain relative overflow-hidden px-5 pb-20 pt-32 sm:px-8 lg:min-h-[100svh] lg:pt-40" data-testid="home-hero">
      <motion.div style={{ y: yBg }} className="absolute inset-0"><TrackLines /></motion.div>
      <div className="relative mx-auto grid max-w-[1400px] items-center gap-14 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }} className="mb-8 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
            <FlagRow size="w-[22px]" />
            <Eyebrow className="text-[#3d3a35]">it staffing · one team, two wings</Eyebrow>
          </motion.div>
          <MaskLines onLoad testid="hero-title"
            lines={["the best hire", "you haven't made", <>is <Em className="text-[#FF4D1A]">warming up.</Em></>]}
            className="font-display text-[2.85rem] font-bold leading-[0.9] tracking-[-0.04em] sm:text-7xl lg:text-[6.6rem]" />
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8, duration: 0.8 }}
            className="mt-9 max-w-[34rem] text-base leading-relaxed text-[#2c2a26] md:text-lg">
            Somewhere right now, a sharp engineer is one phone call from the role they were built for. We make that call.
            You hear from us within the hour, and you see names before the day is out.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.8 }} className="mt-10 flex flex-wrap items-center gap-4">
            <MagneticButton to="/clients" tone="ink" testid="hero-hire-btn">i need people</MagneticButton>
            <MagneticButton to="/talent" tone="ghost" testid="hero-talent-btn">i'm the people</MagneticButton>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 }} className="mt-10 flex flex-wrap gap-3">
            <Chip k="01h" v="first call back" tone="bg-[#F1ECE2]" testid="sla-chip-1h" />
            <Chip k="05h" v="first shortlist" tone="bg-[#D4FF3D]" testid="sla-chip-5h" />
          </motion.div>
        </div>
        <motion.div style={{ y: yWatch }} className="lg:col-span-5"><TiltWatch /></motion.div>
      </div>
    </section>
  );
};
